import React, { useEffect, useState } from 'react';
import { Download, Play } from 'lucide-react';
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import {
  rowMenuTransitionClass,
  rowMenuTriggerClass,
} from "./RowActionsMenu";
import { cn } from "../../DashboardPage/lib/utils";
import Button from "./Button";
import { formCheckboxClass } from "./formStyles";
import type { PlaylistRowHonesty } from "../../repositories/playlists";
import { stalePositionText } from "../playlists/stalePlacements";


// Match your actual data structure from analytics.tsx
interface Playlist extends PlaylistRowHonesty {
  playlist: {
    uuid: string;
    name: string;
    imageUrl: string;
    platform: string;
    owner?: { name: string };
    type?: string;
  };
  peakPosition: number;
  position: number;
  entryDate: string;
  exitDate?: string;
  positionDate?: string;
  peakPositionDate?: string;
  isEditorial: boolean;
  playlistType: "editorial" | "algorithmic" | "algotorial";
}

// CSV Export data structure (only needed fields for export)
interface CSVExportData {
  playlist: string;
  platform: string;
  type: string;
  peakPosition: number;
  /** A number, or "#12 · last confirmed …" for a stale placement, as the table shows it. */
  currentPosition: number | string;
  exitDate: string;
  entryDate: string;
}

interface CSVDownloadProps {
  activePlatform: string;
  /**
   * Rows to export, as currently shown. Previously this component read an unbounded
   * localStorage blob that accumulated across every track the user had ever opened, so an
   * export could carry rows from songs they were not looking at.
   */
  rows: Playlist[];
}


interface MusicPlatform {
  name: string;
  code: string;
}


const CSVDownload: React.FC<CSVDownloadProps> = ({ activePlatform, rows }) => {

  const [platforms, setPlatforms] = useState<MusicPlatform[]>([]);
  const [data, setData] = useState<Playlist[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportPlatforms, setExportPlatforms] = useState<string[]>([]);

  const [selectedPlatforms, setSelectedPlatforms] = useState<MusicPlatform[]>([]); // Default to Spotify

  const onPlatformChange = (e: any) => {
    let _selectedPlatforms = [...selectedPlatforms];

    if (e.checked) {
      _selectedPlatforms.push(e.value);
    } else {
      _selectedPlatforms = _selectedPlatforms.filter(platform => platform.code !== e.value.code);
    }

    setSelectedPlatforms(_selectedPlatforms);
  };

  const musicPlatforms: MusicPlatform[] = [
    { name: 'Spotify', code: 'spotify' },
    { name: 'Apple Music', code: 'apple' },
    { name: 'Amazon Music', code: 'amazon' },
    { name: 'Deezer', code: 'deezer' },
    { name: 'SoundCloud', code: 'soundcloud' },
    { name: 'TIDAL', code: 'tidal' },
    { name: 'Audiomack', code: 'audiomack' }
  ];


  // Export what is on screen. The rows arrive already deduplicated by the backend, so the CSV
  // matches the table rather than carrying the duplicate copies SoundCharts returns per market.
  useEffect(() => {
    setData(rows);
    setPlatforms(activePlatform ? [{ code: activePlatform, name: activePlatform }] : []);
  }, [rows, activePlatform]);



  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const transformToCSVData = (items: Playlist[]): CSVExportData[] => {
    return items.filter((item) => selectedPlatforms.some(platform => platform.code === item.playlist.platform)).map(item => ({
      playlist: item.playlist.name,
      platform: item.playlist.platform,
      type: item.playlistType,
      peakPosition: item.peakPosition,
      currentPosition: item.isStale ? stalePositionText(item, formatDate) : item.position,
      exitDate: item.exitDate ? formatDate(item.exitDate) : '',
      entryDate: formatDate(item.entryDate)
    }));
  };  

  const downloadCSV = async (): Promise<void> => {
    setIsExporting(true);

    try {
      const csvData = transformToCSVData(data);

      const headers: string[] = [
        'Playlist',
        'Platform',
        'Type',
        'Peak_Position',
        'Current_Position',
        'Exit_Date',
        'Entry_Date'
      ];

      // const csvContent = [
      //   headers.join(','),
      //   ...csvData.map((item: CSVExportData) => [
      //     `"${item.playlist}"`,
      //     item.platform,
      //     item.type,
      //     item.peakPosition.toString(),
      //     item.currentPosition.toString(),
      //     item.exitDate,
      //     item.entryDate
      //   ].join(','))
      // ].join('\n');

      const delimiter = ';'; // 👈 Use semicolon for Excel compatibility

      const csvContent = [
        headers.join(delimiter),
        ...csvData.map((item: CSVExportData) => [
          `"${item.playlist}"`,
          item.platform,
          item.type,
          item.peakPosition.toString(),
          item.currentPosition.toString(),
          item.exitDate,
          item.entryDate
        ].join(delimiter))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `playlists-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV download failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className={rowMenuTriggerClass}>
              <EllipsisVerticalIcon className="w-5 h-5" />
            </Menu.Button>
            <Menu.Items
              modal={false}
              as="div"
              transition
              anchor={{ to: "bottom end", gap: 4 }}
              className={cn(
                "z-50 p-4 bg-white border divide-y shadow-lg divide-neutral-100 border-neutral-200 min-w-96 rounded-xl focus:outline-none",
                rowMenuTransitionClass
              )}
            >
                <div className='grid grid-cols-2 gap-4'>
                  {platforms.map((platform) => {
                    return (
                      <div key={platform.code} className="flex items-center">
                        {/* The app's standard checkbox. Was PrimeReact's, which with no
                            PrimeReact theme loaded fell back to the browser default. */}
                        <input
                          type="checkbox"
                          id={platform.code}
                          name="platform"
                          className={formCheckboxClass}
                          onChange={(e) => onPlatformChange({ checked: e.target.checked, value: platform })}
                          checked={selectedPlatforms.some((item) => item.code === platform.code)}
                        />
                        <label htmlFor={platform.code} className="ml-2 text-sm cursor-pointer text-neutral-700">
                          {/* The active platform arrives as its code ("spotify"); show its display name. */}
                          {musicPlatforms.find((p) => p.code === platform.code)?.name ?? platform.name}
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className='flex justify-end mt-4 '>
                  <Button
                    onClick={downloadCSV}
                    disabled={selectedPlatforms.length === 0 || data.length === 0 || isExporting}
                    className="mt-4"
                  >
                    <Download className="flex-shrink-0 w-4 h-4" />
                    {isExporting ? 'Exporting...' : `Export CSV`}
                  </Button>

                </div>
            </Menu.Items>
      </Menu>
    </div>
  );
};

export default CSVDownload;